import React, { useState } from "react";
import styled from "styled-components";
import { colors, UI_CAPTION, UI_CAPTION_BOLD, icons } from "oolib";
import LexicalTextEditor from "@/components/inputs/TextEditor/LexicalTextEditor";

/**
 * Providers report the detected language in different formats — Sarvam returns
 * BCP-47 ("hi-IN"), ElevenLabs returns ISO-639-3 ("ind", "hin"). Render a human
 * readable name instead of the raw code, falling back to the code itself if the
 * runtime can't resolve it.
 */
const getLanguageLabel = (languageCode) => {
  if (!languageCode) return "";
  try {
    const name = new Intl.DisplayNames([navigator.language || "en"], {
      type: "language",
      fallback: "none",
    }).of(languageCode);
    return name || languageCode;
  } catch {
    return languageCode;
  }
};

const Wrapper = styled.div`
  margin-top: 0.75rem;
  border: 1px solid ${colors.greyColor15};
  border-radius: 6px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: ${colors.greyColor5};
  cursor: pointer;
  user-select: none;
`;

const Content = styled.div`
  max-height: ${({ expanded }) => (expanded ? "none" : "120px")};
  overflow-y: ${({ expanded }) => (expanded ? "auto" : "hidden")};
  position: relative;
  padding: 0.75rem;

  ${({ expanded }) =>
    !expanded &&
    `
    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 40px;
      background: linear-gradient(transparent, white);
      pointer-events: none;
    }
  `}
`;

const Section = styled.div`
  &:not(:first-child) {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid ${colors.greyColor10};
  }
`;

const ExpandIcon = styled.span`
  transform: ${({ expanded }) => (expanded ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
`;

/**
 * Displays transcription and/or translation results beneath the audio player.
 * Collapsed to ~120px by default with a fade-out gradient; click header to expand.
 *
 * Props:
 *   transcriptionData: {
 *     status: "completed" | "failed",
 *     result?: { original, translated, diarized, diarizedTranslated, languageCode }
 *     error?: string
 *   }
 */
export const TranscriptionDisplay = ({ transcriptionData, content, valuePath }) => {
  const [expanded, setExpanded] = useState(false);

  if (!transcriptionData) return null;

  if (transcriptionData.status === "failed") {
    return (
      <Wrapper>
        <Header>
          <UI_CAPTION color={colors.red}>Transcription failed</UI_CAPTION>
        </Header>
        {transcriptionData.error && (
          <Content expanded>
            <UI_CAPTION color={colors.greyColor60}>{transcriptionData.error}</UI_CAPTION>
          </Content>
        )}
      </Wrapper>
    );
  }

  if (transcriptionData.status !== "completed" || !transcriptionData.result) {
    return null;
  }

  const { finalText, languageCode } = transcriptionData.result;

  if (!finalText?.isLexical) return null;

  return (
    <Wrapper>
      <Header onClick={() => setExpanded(!expanded)}>
        <UI_CAPTION_BOLD color={colors.greyColor80}>
          Transcription{languageCode ? ` (${getLanguageLabel(languageCode)})` : ""}
        </UI_CAPTION_BOLD>
        <ExpandIcon expanded={expanded}>
          <icons.CaretDown size={14} color={colors.greyColor60} />
        </ExpandIcon>
      </Header>

      <Content expanded={expanded}>
        <Section>
          <LexicalTextEditor
            value={finalText}
            readOnly
            typo="UI_CAPTION"
            textColor={colors.greyColor80}
            variant="simple"
            annotation={{ enable: true }}
            disableCreateHighlights
            content={content}
            valuePath={valuePath}
          />
        </Section>
      </Content>
    </Wrapper>
  );
};
